// api/pipelines.ts — director-only pipeline (funnel) management endpoints.
// All writes return {funnels: Pipeline[]} — callers replace local state with it.
import { apiRequest } from './client'

export interface PipelineStage {
  id: string
  origin: 'bitrix' | 'native'
  ext_id: string | null
  active: boolean
  display_name: string
  sort: number
  color: string
  semantics: string
  hidden: boolean
}

export interface Pipeline {
  id: string
  name: string
  origin: 'bitrix' | 'native'
  is_default: boolean
  is_archived: boolean
  position: number
  stages: PipelineStage[]
}

export interface PipelinesResponse {
  funnels: Pipeline[]
}

function enc(id: string): string {
  return encodeURIComponent(id)
}

export function fetchPipelines(): Promise<PipelinesResponse> {
  return apiRequest<PipelinesResponse>('/api/miniapp/pipelines')
}

export function importPipelines(): Promise<PipelinesResponse> {
  return apiRequest<PipelinesResponse>('/api/miniapp/pipelines/import', { method: 'POST' })
}

export function createPipeline(name: string): Promise<PipelinesResponse> {
  return apiRequest<PipelinesResponse>('/api/miniapp/pipelines', {
    method: 'POST',
    body: { name },
  })
}

export function patchPipeline(
  pid: string,
  patch: { name?: string; is_default?: boolean; is_archived?: boolean; position?: number },
): Promise<PipelinesResponse> {
  return apiRequest<PipelinesResponse>(`/api/miniapp/pipelines/${enc(pid)}`, {
    method: 'PATCH',
    body: patch,
  })
}

export function createStage(
  pid: string,
  body: { name: string; semantics?: string; color?: string },
): Promise<PipelinesResponse> {
  return apiRequest<PipelinesResponse>(`/api/miniapp/pipelines/${enc(pid)}/stages`, {
    method: 'POST',
    body,
  })
}

export function reorderStages(pid: string, ordered_ids: string[]): Promise<PipelinesResponse> {
  return apiRequest<PipelinesResponse>(`/api/miniapp/pipelines/${enc(pid)}/stages/reorder`, {
    method: 'POST',
    body: { ordered_ids },
  })
}

export function patchStage(
  pid: string,
  sid: string,
  patch: { display_name?: string; sort?: number; color?: string; semantics?: string; hidden?: boolean },
): Promise<PipelinesResponse> {
  return apiRequest<PipelinesResponse>(
    `/api/miniapp/pipelines/${enc(pid)}/stages/${enc(sid)}`,
    { method: 'PATCH', body: patch },
  )
}

export function deleteStage(pid: string, sid: string): Promise<PipelinesResponse> {
  return apiRequest<PipelinesResponse>(
    `/api/miniapp/pipelines/${enc(pid)}/stages/${enc(sid)}`,
    { method: 'DELETE' },
  )
}
